import { Link } from 'react-router-dom';
import { Money } from '../../engine/design/primitives/Misc';
import { formatRelativeDays } from '../../lib/format';
import { INTERVALS, INTERVAL_SHORT, minor } from './totals';

// Grid cells for the quotes list: the trigger-maintained totals live in `row.totals` (jsonb),
// which meta cannot describe as a column, so these render them from the row directly.

type Row = Record<string, unknown>;
type Bucket = { total_minor?: number | string } | undefined;

function totals(row: Row): { one_time?: Bucket; recurring?: Record<string, Bucket> } {
  return (row.totals as { one_time?: Bucket; recurring?: Record<string, Bucket> } | null) ?? {};
}

export function QuoteRefCell({ row }: { row: Row }) {
  return (
    <span className="hf-row" style={{ gap: 6 }}>
      <Link className="hf-link hf-mono" to={`/quotes/${String(row.id)}`}>
        {String(row.ref ?? '')}
      </Link>
      {Number(row.revision ?? 1) > 1 && <span className="hf-muted">rev {String(row.revision)}</span>}
    </span>
  );
}

export function OneTimeTotalCell({ row }: { row: Row }) {
  const t = totals(row);
  return <Money minor={String(t.one_time?.total_minor ?? 0)} currency={String(row.currency ?? 'USD')} />;
}

/** The first non-zero recurring bucket with its interval, e.g. "$450.00 /mo". */
export function RecurringTotalCell({ row }: { row: Row }) {
  const t = totals(row);
  const cur = String(row.currency ?? 'USD');
  for (const i of INTERVALS) {
    const v = minor(t.recurring?.[i]?.total_minor ?? 0);
    if (v !== 0n) {
      return (
        <span className="hf-row" style={{ gap: 4 }}>
          <Money minor={v.toString()} currency={cur} />
          <span className="hf-muted">{INTERVAL_SHORT[i]}</span>
        </span>
      );
    }
  }
  return <span className="hf-muted">—</span>;
}

export function ExpiryCell({ row }: { row: Row }) {
  const iso = row.expires_at as string | null;
  const rel = formatRelativeDays(iso);
  if (!rel) return <span className="hf-muted">never</span>;
  const past = iso ? new Date(iso).getTime() < Date.now() : false;
  const open = row.status === 'sent' || row.status === 'viewed';
  return <span className={past && open ? 'hf-money neg' : undefined}>{rel}</span>;
}
